<template>
  <div class="experiment-wrapper">
    <div class="sidebar">
      <div class="controls">
        <h3>Параметры эксперимента</h3>

        <div class="control-group">
          <label>Высота падения h (м):</label>
          <input type="range" min="0.5" max="5.0" step="0.1" v-model.number="dropHeight" />
          <span class="value">{{ dropHeight.toFixed(1) }}</span>
        </div>

        <div class="control-group">
          <label>Масса тела m (кг):</label>
          <input type="range" min="0.1" max="5.0" step="0.1" v-model.number="ballMass" />
          <span class="value">{{ ballMass.toFixed(1) }}</span>
        </div>

        <div class="control-group">
          <label>Материал тела:</label>
          <select v-model="ballMaterial">
            <option v-for="(m, key) in ballMaterials" :key="key" :value="key">{{ m.name }}</option>
          </select>
        </div>

        <div class="control-group">
          <label>Поверхность:</label>
          <select v-model="groundMaterial">
            <option v-for="(m, key) in groundMaterials" :key="key" :value="key">{{ m.name }}</option>
          </select>
        </div>

        <div class="control-group checkbox">
          <input type="checkbox" id="airRes" v-model="airResistance" />
          <label for="airRes">Сопротивление воздуха</label>
        </div>

        <div class="buttons">
          <button class="btn-primary" @click="start" :disabled="running && !paused">Запустить</button>
          <button class="btn-secondary" @click="pause" :disabled="!running">{{ paused ? 'Продолжить' : 'Пауза' }}</button>
          <button class="btn-reset" @click="reset">Сбросить</button>
        </div>
      </div>

      <div class="info">
        <h3>Текущие значения</h3>
        <div class="info-row"><span>t</span><span>{{ time.toFixed(2) }} с</span></div>
        <div class="info-row"><span>v</span><span>{{ speedMps.toFixed(2) }} м/с</span></div>
        <div class="info-row"><span>h</span><span>{{ currentHeight.toFixed(2) }} м</span></div>
        <div class="info-row"><span>Eпот</span><span>{{ ePot.toFixed(2) }} Дж</span></div>
        <div class="info-row"><span>Eкин</span><span>{{ eKin.toFixed(2) }} Дж</span></div>
        <div class="info-row"><span>Eполн</span><span>{{ eTotalEnergy.toFixed(2) }} Дж</span></div>
      </div>

      <div class="theory">
        <h3>Теория (без сопр. воздуха)</h3>
        <div class="info-row"><span>tпад</span><span>{{ theoreticalFallTime.toFixed(2) }} с</span></div>
        <div class="info-row"><span>vудар</span><span>{{ theoreticalImpactSpeed.toFixed(2) }} м/с</span></div>
        <div class="info-row"><span>hотск</span><span>{{ theoreticalBounceHeight.toFixed(2) }} м</span></div>
      </div>
    </div>

    <div class="canvas-box">
      <canvas ref="worldCanvas" :width="canvasWidth" :height="canvasHeight"></canvas>
      <div class="timeline" v-if="frames.length > 1">
        <input
          type="range"
          min="0"
          :max="frames.length - 1"
          v-model.number="frameIndex"
          @input="onScrub"
        />
        <span class="time-label">{{ time.toFixed(2) }} / {{ maxTime }} с</span>
      </div>
      <div class="legend">
        <span>Масштаб: 1 м = {{ pixelsPerMeter }} px</span>
        <span>g = {{ g }} м/с²</span>
      </div>
    </div>
  </div>
</template>

<script>
const BALL_MATERIALS = {
  rubber: { name: 'Резина', density: 1100, restitution: 0.85, color: '#e74c3c' },
  wood: { name: 'Дерево', density: 600, restitution: 0.3, color: '#d4a373' },
  metal: { name: 'Металл', density: 7800, restitution: 0.05, color: '#95a5a6' },
  tennis: { name: 'Теннисный мяч', density: 80, restitution: 0.75, color: '#ccff00' },
};

const GROUND_MATERIALS = {
  wood: { name: 'Дерево', restitution: 0.5, color: '#8b5a2b' },
  concrete: { name: 'Бетон', restitution: 0.2, color: '#7f8c8d' },
  marble: { name: 'Мрамор', restitution: 0.05, color: '#bdc3c7' },
  rubber: { name: 'Резина', restitution: 0.9, color: '#e74c3c' },
};

export default {
  name: 'NewtonExperiment',
  data() {
    return {
      ballMaterials: BALL_MATERIALS,
      groundMaterials: GROUND_MATERIALS,
      // Parameters
      dropHeight: 2.0,
      ballMass: 1.0,
      ballMaterial: 'rubber',
      groundMaterial: 'wood',
      airResistance: false,
      // Simulation state
      running: false,
      paused: false,
      time: 0,
      y: 0,       // ball center Y in px
      vy: 0,      // ball velocity Y in px/s
      trajectory: [],
      lastTrajTime: 0,
      animFrame: null,
      maxTime: 10,
      frames: [],
      frameIndex: 0,
      isScrubbing: false,
      // Constants
      g: 9.81,
      pixelsPerMeter: 100,
      groundY: 550,
      canvasWidth: 800,
      canvasHeight: 600,
      ballX: 400,
      rhoAir: 1.225,
      Cd: 0.47,
      minRadiusPx: 8,
      maxRadiusPx: 55,
    };
  },
  computed: {
    ballMat() {
      return this.ballMaterials[this.ballMaterial];
    },
    groundMat() {
      return this.groundMaterials[this.groundMaterial];
    },
    ballRadiusMeters() {
      // m = ρ * (4/3)πr³  =>  r = ³√(3m / 4πρ)
      const r = Math.cbrt((3 * this.ballMass) / (4 * Math.PI * this.ballMat.density));
      return r;
    },
    ballRadiusPx() {
      let r = this.ballRadiusMeters * this.pixelsPerMeter;
      return Math.max(this.minRadiusPx, Math.min(this.maxRadiusPx, r));
    },
    eTotal() {
      // Effective restitution coefficient (geometric mean)
      const e1 = this.ballMat.restitution;
      const e2 = this.groundMat.restitution;
      return Math.sqrt(e1 * e2);
    },
    currentHeight() {
      return Math.max(0, (this.groundY - this.y) / this.pixelsPerMeter);
    },
    speedMps() {
      return this.vy / this.pixelsPerMeter;
    },
    ePot() {
      return this.ballMass * this.g * this.currentHeight;
    },
    eKin() {
      const v = this.speedMps;
      return 0.5 * this.ballMass * v * v;
    },
    eTotalEnergy() {
      return this.ePot + this.eKin;
    },
    theoreticalFallTime() {
      return Math.sqrt((2 * this.dropHeight) / this.g);
    },
    theoreticalImpactSpeed() {
      return Math.sqrt(2 * this.g * this.dropHeight);
    },
    theoreticalBounceHeight() {
      return this.dropHeight * this.eTotal * this.eTotal;
    },
  },
  watch: {
    dropHeight() { if (!this.running) this.reset(); },
    ballMass() { if (!this.running) this.reset(); },
    ballMaterial() { if (!this.running) this.reset(); },
    groundMaterial() { if (!this.running) this.reset(); },
  },
  mounted() {
    this.reset();
    this.loop();
  },
  beforeUnmount() {
    cancelAnimationFrame(this.animFrame);
  },
  methods: {
    reset() {
      this.running = false;
      this.paused = false;
      this.isScrubbing = false;
      this.time = 0;
      this.vy = 0;
      this.trajectory = [];
      this.lastTrajTime = 0;
      this.frames = [];
      this.frameIndex = 0;
      this.y = this.groundY - this.dropHeight * this.pixelsPerMeter;
      this.draw();
    },
    recordFrame() {
      this.frames.push({
        time: this.time,
        y: this.y,
        vy: this.vy,
        trajectory: this.trajectory.slice(),
      });
    },
    scrubTo(index) {
      const f = this.frames[index];
      if (!f) return;
      this.time = f.time;
      this.y = f.y;
      this.vy = f.vy;
      this.trajectory = f.trajectory.slice();
      this.draw();
    },
    onScrub() {
      this.isScrubbing = true;
      this.running = false;
      this.paused = false;
      this.scrubTo(this.frameIndex);
    },
    start() {
      if (this.running && this.paused) {
        this.paused = false;
        this.isScrubbing = false;
        return;
      }
      // Continue from scrubbed position if in the middle of timeline
      if (this.frames.length > 0 && this.frameIndex < this.frames.length - 1) {
        const f = this.frames[this.frameIndex];
        this.time = f.time;
        this.y = f.y;
        this.vy = f.vy;
        this.trajectory = f.trajectory.slice();
        this.frames = this.frames.slice(0, this.frameIndex + 1);
        this.running = true;
        this.paused = false;
        this.isScrubbing = false;
        return;
      }
      this.reset();
      this.running = true;
      this.isScrubbing = false;
    },
    pause() {
      if (!this.running) return;
      this.paused = !this.paused;
      this.isScrubbing = false;
    },
    update(dt) {
      // Gravity acceleration in px/s²
      let ay = this.g * this.pixelsPerMeter;

      if (this.airResistance) {
        const vMag = Math.abs(this.vy) / this.pixelsPerMeter; // м/с
        if (vMag > 0) {
          const area = Math.PI * this.ballRadiusMeters * this.ballRadiusMeters;
          const fDrag = 0.5 * this.rhoAir * this.Cd * area * vMag * vMag;
          const aDrag = (fDrag / this.ballMass) * this.pixelsPerMeter; // px/s²
          ay -= Math.sign(this.vy) * aDrag;
        }
      }

      this.vy += ay * dt;
      this.y += this.vy * dt;
      this.time += dt;

      const r = this.ballRadiusPx;

      // Ground collision
      if (this.y + r >= this.groundY) {
        this.y = this.groundY - r;
        this.vy = -this.vy * this.eTotal;
        // Stop micro-bouncing
        if (Math.abs(this.vy) < 5) {
          this.vy = 0;
        }
      }

      // Ceiling (safety)
      if (this.y - r < 0) {
        this.y = r;
        this.vy = Math.abs(this.vy) * 0.5;
      }

      // Trajectory recording
      if (this.time - this.lastTrajTime > 0.04) {
        this.trajectory.push({ x: this.ballX, y: this.y });
        this.lastTrajTime = this.time;
      }
      if (this.trajectory.length > 400) {
        this.trajectory.shift();
      }

      this.recordFrame();
      this.frameIndex = this.frames.length - 1;

      if (this.time >= this.maxTime) {
        this.running = false;
        this.paused = false;
      }
    },
    loop() {
      if (this.running && !this.paused && !this.isScrubbing) {
        this.update(1 / 60);
      }
      this.draw();
      this.animFrame = requestAnimationFrame(this.loop);
    },
    // ===== Drawing =====
    // ===== Drawing =====
    draw() {
      const canvas = this.$refs.worldCanvas;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, this.canvasWidth, this.canvasHeight);

      this.drawBackground(ctx);
      this.drawRuler(ctx);
      this.drawGround(ctx);
      this.drawTrajectory(ctx);
      this.drawBall(ctx);
      this.drawSpeedVector(ctx);
    },
    drawBackground(ctx) {
      const grad = ctx.createLinearGradient(0, 0, 0, this.canvasHeight);
      grad.addColorStop(0, '#0f172a');
      grad.addColorStop(0.6, '#1e293b');
      grad.addColorStop(1, '#334155');
      ctx.fillStyle = grad;
      ctx.fillRect(0, 0, this.canvasWidth, this.canvasHeight);
    },
    drawRuler(ctx) {
      ctx.strokeStyle = 'rgba(255,255,255,0.2)';
      ctx.lineWidth = 1;
      ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.font = '12px Arial';
      ctx.textAlign = 'right';

      for (let m = 0; m <= 6; m++) {
        const y = this.groundY - m * this.pixelsPerMeter;
        if (y < 0) break;
        ctx.beginPath();
        ctx.moveTo(this.canvasWidth - 80, y);
        ctx.lineTo(this.canvasWidth, y);
        ctx.stroke();
        ctx.fillText(m + ' м', this.canvasWidth - 85, y + 4);
      }
    },
    drawGround(ctx) {
      ctx.fillStyle = this.groundMat.color;
      ctx.fillRect(0, this.groundY, this.canvasWidth, this.canvasHeight - this.groundY);
      // Texture line
      ctx.strokeStyle = 'rgba(0,0,0,0.15)';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(0, this.groundY);
      ctx.lineTo(this.canvasWidth, this.groundY);
      ctx.stroke();
    },
    drawTrajectory(ctx) {
      if (this.trajectory.length < 2) return;
      ctx.beginPath();
      ctx.strokeStyle = 'rgba(255, 235, 59, 0.6)';
      ctx.lineWidth = 2;
      ctx.moveTo(this.trajectory[0].x, this.trajectory[0].y);
      for (let i = 1; i < this.trajectory.length; i++) {
        ctx.lineTo(this.trajectory[i].x, this.trajectory[i].y);
      }
      ctx.stroke();
    },
    drawBall(ctx) {
      const r = this.ballRadiusPx;
      const x = this.ballX;
      const y = this.y;

      // Shadow
      ctx.beginPath();
      ctx.ellipse(x, this.groundY - 2, r * 0.8, r * 0.2, 0, 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(0,0,0,0.25)';
      ctx.fill();

      // Body
      ctx.beginPath();
      ctx.arc(x, y, r, 0, Math.PI * 2);
      const grad = ctx.createRadialGradient(x - r * 0.3, y - r * 0.3, r * 0.1, x, y, r);
      grad.addColorStop(0, lighten(this.ballMat.color, 40));
      grad.addColorStop(1, this.ballMat.color);
      ctx.fillStyle = grad;
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.3)';
      ctx.lineWidth = 1;
      ctx.stroke();
    },
    drawSpeedVector(ctx) {
      const v = this.vy / this.pixelsPerMeter; // m/s
      if (Math.abs(v) < 0.1) return;
      const scale = 30; // px per m/s
      const x = this.ballX + this.ballRadiusPx + 15;
      const y = this.y;
      const len = -v * scale; // negative because canvas Y grows downward

      ctx.beginPath();
      ctx.moveTo(x, y);
      ctx.lineTo(x, y + len);
      ctx.strokeStyle = '#4ade80';
      ctx.lineWidth = 2;
      ctx.stroke();

      // Arrowhead
      const angle = Math.atan2(len, 0);
      const headLen = 8;
      ctx.beginPath();
      ctx.moveTo(x, y + len);
      ctx.lineTo(x - headLen * Math.cos(angle - Math.PI / 6), y + len - headLen * Math.sin(angle - Math.PI / 6));
      ctx.lineTo(x - headLen * Math.cos(angle + Math.PI / 6), y + len - headLen * Math.sin(angle + Math.PI / 6));
      ctx.lineTo(x, y + len);
      ctx.fillStyle = '#4ade80';
      ctx.fill();

      // Label
      ctx.font = '12px Arial';
      ctx.fillStyle = '#4ade80';
      ctx.textAlign = 'left';
      ctx.fillText(v.toFixed(1) + ' м/с', x + 6, y + len / 2);
    },
  },
};

// Helper to lighten hex color
function lighten(hex, percent) {
  const num = parseInt(hex.replace('#', ''), 16);
  const amt = Math.round(2.55 * percent);
  const R = Math.min(255, (num >> 16) + amt);
  const G = Math.min(255, ((num >> 8) & 0x00ff) + amt);
  const B = Math.min(255, (num & 0x0000ff) + amt);
  return '#' + (0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1);
}
</script>

<style scoped>
.experiment-wrapper {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  padding: 20px;
  justify-content: center;
  background: #0b0f19;
  min-height: 100vh;
  color: #e2e8f0;
}

.sidebar {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 320px;
}

.controls,
.info,
.theory {
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  padding: 16px;
}

.controls h3,
.info h3,
.theory h3 {
  margin: 0 0 12px 0;
  font-size: 15px;
  color: #94a3b8;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.control-group {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  flex-wrap: wrap;
}

.control-group label {
  font-size: 13px;
  color: #cbd5e1;
  min-width: 120px;
}

.control-group input[type='range'] {
  flex: 1;
  min-width: 100px;
}

.control-group input[type='number'],
.control-group select {
  width: 80px;
  padding: 4px 6px;
  border-radius: 4px;
  border: 1px solid #475569;
  background: #1e293b;
  color: #e2e8f0;
  font-size: 13px;
}

.control-group .value {
  font-weight: bold;
  color: #38bdf8;
  min-width: 40px;
  text-align: right;
  font-size: 13px;
}

.control-group.checkbox {
  gap: 6px;
}

.control-group.checkbox input {
  width: 16px;
  height: 16px;
}

.buttons {
  display: flex;
  gap: 8px;
  margin-top: 8px;
}

button {
  flex: 1;
  padding: 8px 0;
  border-radius: 6px;
  border: 1px solid transparent;
  font-size: 13px;
  cursor: pointer;
  transition: opacity 0.2s;
}

button:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.btn-primary {
  background: #2563eb;
  color: white;
  border-color: #2563eb;
}

.btn-secondary {
  background: #334155;
  color: #e2e8f0;
  border-color: #475569;
}

.btn-reset {
  background: #7f1d1d;
  color: #fee2e2;
  border-color: #991b1b;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  font-size: 13px;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row span:first-child {
  color: #94a3b8;
}

.info-row span:last-child {
  font-weight: bold;
  color: #e2e8f0;
  font-family: 'Courier New', monospace;
}

.canvas-box {
  position: relative;
}

.canvas-box canvas {
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: #0f172a;
}

.timeline {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 10px;
  padding: 8px 12px;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;
}

.timeline input[type='range'] {
  flex: 1;
  cursor: pointer;
}

.timeline .time-label {
  font-size: 13px;
  color: #94a3b8;
  font-family: 'Courier New', monospace;
  min-width: 90px;
  text-align: right;
}

.legend {
  display: flex;
  gap: 16px;
  margin-top: 8px;
  font-size: 12px;
  color: #64748b;
  justify-content: center;
}
</style>
